<template>
  <div id="artwork-list">
    <div class="artwork-filters w-100">
      <div class="row">
        <div class="col-12 col-md-4 mb-1">
          <div v-if="!(lockSuitability && !feedSuitableOnly)">
            <ss-checkbox id="feed-suitable"
                         v-model="feed_suitable_only"
                         :disabled="lockSuitability">
              Suitable for {{ getFeedLabel(true) }}
            </ss-checkbox>
            <button type="button" class="link-like d-inline ml-2">
              <i class="info-modal-btn la la-question-circle"
                 title="Click for details"
                 @click="showFeedSuitabilityModal" />
            </button>
          </div>
          <div v-if="!(lockSuitability && !bannerSuitableOnly)" v-perms="['podcast_websites']">
            <ss-checkbox id="feed-banner-suitable"
                         v-model="banner_suitable_only"
                         :disabled="lockSuitability">
              Suitable for {{ getFeedLabel(true) }} Banners
            </ss-checkbox>
            <button type="button" class="link-like d-inline ml-2">
              <i class="info-modal-btn la la-question-circle"
                 title="Click for details"
                 @click="showBannerSuitabilityModal" />
            </button>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-md-4">
          <div class="form-group">
            <div class="input-group">
              <input v-model="name_filter" type="text"
                     class="form-control"
                     placeholder="Search name"
                     @keypress.enter="fetchArtworks">
              <div class="input-group-append">
                <button class="btn btn-ss" type="button"
                        @click="fetchArtworks">
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-4">
          <div class="form-group">
            <multiselect v-model="tag_filter"
                         :options="existing_tags"
                         track-by="name"
                         label="name"
                         :searchable="true"
                         :show-labels="true"
                         placeholder="Filter by Tag"
                         @input="fetchArtworks" />
          </div>
        </div>
        <div class="col-12 col-md-4">
          <div class="form-group">
            <b-dropdown block>
              <template #button-content>
                <i class="fa fa-sort" />{{ sort_by_text }}
              </template>
              <b-dropdown-item-button
                v-for="sort_field in sort_fields"
                :key="sort_field.field"
                @click="() => sortBy(sort_field)">
                <span class="sort-fields" :class="[sort_field.active ? `active` : ``]">
                  <i v-if="sort_field.active" class="fa"
                     :class="[ `fa-sort-${sort_field.sort_dir === `asc` ? `up` : `down`}` ]" />{{ sort_field.display }}
                </span>
              </b-dropdown-item-button>
            </b-dropdown>
          </div>
        </div>
      </div>
    </div>
    <div class="artwork-images w-100">
      <div class="row d-flex flex-column align-items-center" style="margin: 0; max-width: 100%;">
        <div v-if="suitable_artworks.length"
             class="col-12 text-center load-controls mb-2">
          Showing {{ suitable_artworks.length }} of {{ total_artworks }}
          <button v-if="can_load_less"
                  class="d-inline link-like"
                  @click="() => load_amount = Math.max(20, load_amount - load_increment)">
            Load Less
          </button>
          <button v-if="can_load_more"
                  class="d-inline link-like"
                  @click="() => load_amount += load_increment">
            Load More
          </button>
          <button v-if="can_load_more"
                  class="d-inline link-like"
                  @click="() => load_amount = total_artworks">
            Load All
          </button>
        </div>
        <div class="artworks-display" :class="slim && 'slim'">
          <artwork-uploader
            class="uploader-card"
            :class="slim && 'slim'"
            :dropzone-config="artwork_dropzone_config" />
          <div v-for="artwork in suitable_artworks" :key="artwork.uuid"
               class="artwork-card position-relative"
               :class="slim && 'slim'"
               @click="() => toggleArtworkSelect(artwork)">
            <figure class="d-flex flex-column align-items-center text-center mb-0">
              <img :src="artwork.thumbnail_path || artwork.path" width="100%" :class="{active: highlightSelected && isSelected(artwork)}">
              <figcaption v-if="showNames" class="font-weight-bold py-1" style="font-size: 1.25rem;">
                {{ artwork.original_name ?? artwork.name }}
              </figcaption>
            </figure>
            <!-- edit controls -->
            <div v-if="allowEditing" class="edit-controls position-absolute w-100 h-100 flex-column justify-content-center align-items-center gap-2">
              <div>
                <button type="button" class="btn btn-ss-danger btn-icon" @click="() => destroyArtworks(artwork)">
                  <i class="la la-trash" />
                </button>
                <a href="javascript:;" class="btn btn-light btn-icon" @click="() => copyValueToClipboard(artwork.path)"><i class="la la-link" /></a>
                <button type="button" class="btn btn-light btn-icon" @click="() => showEditModal(artwork)">
                  <i class="la la-edit" />
                </button>
              </div>
              <div class="tags">
                <span v-for="(tag_name, i) in tagNames(artwork)"
                      :key="i"
                      class="kt-badge kt-badge--inline kt-badge--brand tag-name">
                  {{ tag_name }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="!suitable_artworks.length && bannerSuitableOnly" class="row align-items-center pl-4">
            <p class="text-center">
              You have no Artwork suitable for banner images. Upload a landscape image to use as a banner.
            </p>
          </div>
        </div>
        <div v-if="suitable_artworks.length"
             class="col-12 text-center load-controls mt-2"
             @click="fetchArtworks">
          Showing {{ suitable_artworks.length }} of {{ total_artworks }}
          <button v-if="can_load_less"
                  class="d-inline link-like"
                  @click="() => load_amount = Math.max(minLoad, load_amount - load_increment)">
            Load Less
          </button>
          <button v-if="can_load_more"
                  class="d-inline link-like"
                  @click="() => load_amount += load_increment">
            Load More
          </button>
          <button v-if="can_load_more"
                  class="d-inline link-like"
                  @click="() => load_amount = total_artworks">
            Load All
          </button>
        </div>
      </div>
    </div>

    <artwork-feed-suitability-modal />
    <banner-feed-suitability-modal />
    <edit-image-modal v-if="selectedArtwork" :artwork="selectedArtwork" name="edit-image-modal" />
    <delete-confirm-modal entity-type="Artwork" :entities="[selectedArtwork]">
      <template v-if="selectedArtwork">
        <div>You are about to delete Artwork <span class="font-weight-bold">"{{ selectedArtwork.original_name }}"</span></div>
        <img :src="selectedArtwork.thumbnail_path || selectedArtwork.path"
             :alt="selectedArtwork.name"
             class="rounded mt-4"
             style="height: 128px; max-width: 90%">
      </template>
    </delete-confirm-modal>
  </div>
</template>

<script>
import {
  destroyArtworks,
  fetchArtworks,
} from "Lib/artwork";
import {
  fetchTags,
} from "Lib/tags";

import ArtworkUploaderMixin from 'VuePartials/Artwork/ArtworkUploaderMixin';

import ArtworkUploader from "VuePartials/Artwork/ArtworkUploader";
import ArtworkFeedSuitabilityModal from 'VueModals/ArtworkFeedSuitabilityModal';
import BannerFeedSuitabilityModal from 'VueModals/BannerFeedSuitabilityModal';
import DeleteConfirmModal from 'VueComponents/VJSModals/DeleteConfirmModal';
import EditImageModal from "VuePartials/App/Artwork/EditImageModal";

export default {
  components: {
    ArtworkUploader,
    ArtworkFeedSuitabilityModal,
    BannerFeedSuitabilityModal,
    DeleteConfirmModal,
    EditImageModal,
  },
  mixins: [
    ArtworkUploaderMixin,
  ],
  model: {
    prop: 'selectedArtwork',
  },
  props: {
    selectedArtwork: { // v-model
      type: null,
      required: false,
      default: null,
    },
    feedSuitableOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    bannerSuitableOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    lockSuitability: {
      type: Boolean,
      required: false,
      default: false,
    },
    showNames: {
      type: Boolean,
      required: false,
      default: false,
    },
    slim: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    highlightSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    withDetail: {
      type: Boolean,
      required: false,
      default: false,
    },
    minLoad: {
      type: Number,
      required: false,
      default: 20,
    },
    preloadedArtworks: {
      type: Array,
      required: false,
      default: function () {
        return [];
      },
    },
  },
  data() {
    return {
      artworks: this.preloadedArtworks,
      feed_suitable_only: false,
      banner_suitable_only: false,
      selected_sort: {
        sort_dir: 'desc',
        column: 'created_at',
      },
      sort_fields: [
        {
          field: `original_name`,
          display: `Name`,
        },
        {
          field: `created_at`,
          display: `Date created`,
          active: true,
        },
      ],
      name_filter: null,
      tag_filter: null,
      total_artworks: 0,
      load_amount: 30,
      load_increment: 10,
      existing_tags: [],
    };
  },
  computed: {
    sort_by_text() {
      const selected = _.find(this.sort_fields, sort_field => sort_field.field === this.selected_sort.column);
      return `Sorting by ${selected.display} (${this.selected_sort.sort_dir})`;
    },
    can_load_less() {
      return this.load_amount > this.minLoad;
    },
    can_load_more() {
      return this.load_amount < this.total_artworks;
    },
    suitable_artworks() {
      if (!(this.artworks instanceof Array)) return [];

      const f = checks => (artwork) => {
        const detail = artwork.artwork_detail;
        if (detail == null) {
          return false;
        }
        const the_checks = {
          is_square: detail.width === detail.height,
          correct_size_for_feed: (() => {
            const smaller_dim = Math.min(detail.width, detail.height);
            const larger_dim = Math.max(detail.width, detail.height);
            return smaller_dim >= 1400 && larger_dim <= 3000;
          })(),
          correct_format: detail.format.toLowerCase().includes(`png`) || detail.format.toLowerCase().includes('jpeg'),
          is_rgb: detail.colour_space.toLowerCase() === `srgb` || detail.colour_space.toLowerCase() === `rgb`,
          correct_size_for_banner: detail.width >= detail.height * 1.5,
        };
        return checks.every(check => the_checks[check] === true);
      };

      const isFeedSuitable = f([
        'correct_format',
        'is_rgb',
        'correct_size_for_feed',
        'is_square',
      ]);
      const isBannerSuitable = f([
        'correct_format',
        'is_rgb',
        'correct_size_for_banner',
      ]);

      return this.artworks.filter((artwork) => {
        if (this.feed_suitable_only && !isFeedSuitable(artwork)) return false;
        if (this.banner_suitable_only && !isBannerSuitable(artwork)) return false;

        return true;
      });
    },
  },
  watch: {
    load_amount: function () {
      this.fetchArtworks();
    },
    name_filter(new_val) {
      this.name_filter = new_val.replace(/[:|,]/i, '');
    },
  },
  mounted() {
    if (!this.artworks?.length) this.fetchArtworks();

    EventBus.$on("upload-success", () => {
      this.fetchArtworks();
    });
    EventBus.$on("show_artwork_selection_modal", () => {
      this.showModal();
    });

    this.artwork_dropzone_config.dictDefaultMessage = '+';

    this.feed_suitable_only = this.feedSuitableOnly;
    this.banner_suitable_only = this.bannerSuitableOnly;
  },
  methods: {
    isSelected(artwork) {
      return artwork.uuid === this.selectedArtwork?.uuid;
    },
    sortBy(sort_field) {
      const original = this.sort_fields.find(other_field => other_field === sort_field);
      if (original.active) {
        original.sort_dir = original.sort_dir === `asc` ? `desc` : `asc`;
      } else {
        original.sort_dir = 'desc';
      }

      for (const sort_field of this.sort_fields) {
        sort_field.active = false;
      }
      original.active = true;

      this.selected_sort.column = original.field;
      this.selected_sort.sort_dir = original.sort_dir;

      this.fetchArtworks();
    },
    async fetchArtworks() {
      const data = {
        params: {
          sort: `${this.selected_sort.column}|${this.selected_sort.sort_dir}`,
          tags: this.tag_filter?.name ?? '',
          per_page: this.load_amount,
          with_detail: this.withDetail,
        },
      };

      if (this.name_filter) {
        data.params.filters = `name:ct:${this.name_filter}|original_name:ct:${this.name_filter}`;
      }

      this.artworks = [];
      const { artworks, total } = await fetchArtworks({
        request_data: data,
      });
      this.artworks = artworks;
      this.total_artworks = total;

      this.existing_tags = await fetchTags({
        url_params: {
          type: 'artwork',
        },
      });
    },
    async destroyArtworks(...artworks) {
      const confirm = (await this.fireModal('delete-confirm')).data.confirm;
      if (!confirm) return;

      const destroy_result = await destroyArtworks(...artworks);
      if (!destroy_result) return;

      this.fetchArtworks();
    },
    showFeedSuitabilityModal() {
      this.fireModal('artwork-feed-suitability');
    },
    showBannerSuitabilityModal() {
      this.fireModal('banner-feed-suitability');
    },
    showEditModal(artwork) {
      // this.selectedArtwork = artwork;
      this.$nextTick(async () => {
        const { data } = await this.fireModal('edit-image-modal');

        if (!data.update) return;

        const idx_to_splice = this.artworks.findIndex(aw => aw.uuid === artwork.uuid);
        this.artworks.splice(idx_to_splice, 1, data.new_artwork);
        this.existing_tags = [
          ...new Set([
            ...this.existing_tags,
            ...data.new_artwork.tags,
          ]), // use a set to make them unique
        ];
      });
    },
    tagNames(item) {
      return item.tags?.map(tag => tag.name);
    },
    toggleArtworkSelect(artwork) {
      for (const ev of [`input`, `change`]) { // emit for v-model updates
        this.$emit(ev, artwork);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "sass:map";

@use "~AssetsSass/_variables.scss" as *;

.artwork-images {
  .grid-sizer,
  .item {
    width: 150px;
  }

  img.active {
    border:4px solid green;
  }
}

@media (min-width: 768px) {
  .artwork-images {
    .grid-sizer,
    .item {
      width: 150px !important;
    }
  }
}

.show-details-btn {
  font-size: 1.1em;
  font-weight: bold;
  i {
    font-size: 1em;
  }
}

.artworks-display {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: start;

  margin: 0.5em 0;
  gap: 4px;

  width: 100%;

  &.slim {
    grid-template-columns: repeat(6, 1fr);
  }

  .uploader-card {
    margin: 0;
    cursor: pointer;
  }

  .artwork-card {
    max-width: 250px;

    &.slim {
      max-width: 150px;
    }

    .edit-controls {
      display: none;
      top: 0;
      left: 0;
      background-color: color.adjust(#000000, $alpha: -0.7, $space: hsl);
    }

    &:hover {

      .edit-controls {
        display: flex;
        gap: 0.5rem;
      }
    }
  }
}

.load-controls {
  button {
    border-right: 1px solid map.get($ss-theme, 'hunter-grey');
    padding-right: 0.4rem;
    padding-left: 0.2rem;
  }

  button:last-child {
    border: none;
  }
}
</style>
